// frontend/src/components/QRCodeModal.tsx

import { X, Download, Printer } from "lucide-react";
import { useTranslation } from "react-i18next";

interface QRCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  qrCodeUrl: string;
  eventName: string;
  eventCode: string;
}

export default function QRCodeModal({
  isOpen,
  onClose,
  qrCodeUrl,
  eventName,
  eventCode,
}: QRCodeModalProps) {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = qrCodeUrl;
    link.download = `QR_Code_${eventName.replace(/\s+/g, "_")}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>${t("common.print")} QR Code - ${eventName}</title>
          <style>
            body { 
              font-family: sans-serif; 
              display: flex; 
              flex-direction: column; 
              align-items: center; 
              justify-content: center; 
              height: 100vh; 
              margin: 0;
            }
            .container { text-align: center; border: 2px solid #eee; padding: 40px; border-radius: 20px; }
            h1 { font-size: 32px; margin-bottom: 10px; }
            p { font-size: 20px; color: #666; margin-bottom: 30px; }
            img { width: 400px; height: 400px; }
            .footer { margin-top: 40px; font-size: 14px; color: #999; }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>${eventName}</h1>
            <p>${t("home.scanQr")}</p>
            <img src="${qrCodeUrl}" />
            <div class="footer">${t("admin.dashboard.eventCode")} : ${eventCode}</div>
          </div>
          <script>
            window.onload = () => {
              window.print();
              setTimeout(() => { window.close(); }, 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-bold text-gray-900">{t("modal.qr.title")}</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 text-center">
          <div className="bg-gray-50 rounded-xl p-3 mb-2 inline-block">
            <img
              src={qrCodeUrl}
              alt="QR Code"
              className="w-56 h-56 mx-auto"
            />
          </div>
          <h4 className="text-xl font-bold text-gray-900 mb-1">{eventName}</h4>
          <p className="text-xs text-gray-500 mb-4 font-medium">
            {t("admin.dashboard.eventCode")} : <span className="text-sky-700">{eventCode}</span>
          </p>

          {/* Actions */}
          <div className="grid grid-cols-2 gap-3 px-2">
            <button
              onClick={handleDownload}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-all text-sm"
            >
              <Download className="w-4 h-4" />
              {t("common.download")}
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-all shadow-md text-sm"
            >
              <Printer className="w-4 h-4" />
              {t("common.print")}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="py-3 px-4 bg-gray-50 text-center">
          <p className="text-[9px] text-gray-400 uppercase tracking-widest font-bold">
            KDevs
          </p>
        </div>
      </div>
    </div>
  );
}
